/**
 * Network Utilities
 * Guards and helpers for network status and authentication
 */

/**
 * Check if device is online
 */
export const isOnline = () => {
  if (typeof navigator === 'undefined') return false;
  return navigator.onLine;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (authToken, orgToken) => {
  return !!(authToken && orgToken);
};

/**
 * Check if API call should be made
 * Returns true only if online AND authenticated
 */
export const shouldMakeAPICall = (authToken, orgToken) => {
  return isOnline() && isAuthenticated(authToken, orgToken);
};

/**
 * Get network status
 */
export const getNetworkStatus = () => {
  return {
    online: isOnline(),
    timestamp: Date.now(),
  };
};

/**
 * Listen to online/offline events
 */
export const onNetworkStatusChange = (
  onOnline,
  onOffline
) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

/**
 * Safe API call wrapper
 * Only executes if online and authenticated
 */
export const safeAPICall = async (
  apiCall,
  authToken,
  orgToken,
  fallback
) => {
  if (!shouldMakeAPICall(authToken, orgToken)) {
    console.warn('⚠️ API call skipped - offline or not authenticated');
    return fallback || null;
  }

  try {
    return await apiCall();
  } catch (error) {
    // Never throw network errors
    if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
      console.warn('⚠️ Network error caught, using fallback');
      return fallback || null;
    }
    // Re-throw non-network errors
    throw error;
  }
};
